import React, {ReactElement} from "react";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import Checkbox from "../../../../../../components/fields/Checkbox";
import {t} from "../../../../../../store/localization/TranslMessasge";
import elementService from "../../../elementdetail/service/ElementService";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";
import {CheckboxField} from "../model/CheckboxField";
import {UiComponent} from "../model/UiComponent";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {PropertyValuePersister} from "./PropertyValuePersister";
import {ValuePersister} from "./ValuePersister";

export class CheckboxFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["CheckboxField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValue(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return "";
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const checkboxField = component as CheckboxField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[checkboxField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        let value = stateContext.values[checkboxField.attributeDefinitionId];
        if (value !== undefined && value !== null) {
            value = value.toString();
        }
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, stateContext, dataContext}: UiComponentRenderingProps): ReactElement {
        const checkboxField = component as CheckboxField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[checkboxField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={checkboxField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[checkboxField.attributeDefinitionId] === "true";
        const readOnly = checkboxField.readOnly || !stateContext.editMode;

        if (!readOnly && stateContext.values[checkboxField.attributeDefinitionId] === undefined) {
            stateContext.onValuesChange({[checkboxField.attributeDefinitionId]: value});
        }

        function onChange(checked: boolean) {
            if (!readOnly) {
                stateContext.onValuesChange({[checkboxField.attributeDefinitionId]: "" + checked})
            }
        }

        return (
                <Checkbox
                        key={checkboxField.id}
                        label={attributeDefinition.name}
                        checked={value}
                        onChange={onChange}
                        readOnly={readOnly}/>
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const checkboxField = component as CheckboxField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[checkboxField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[checkboxField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}